import { Link } from 'react-router-dom'
import { useDashboard, useEngagements, useFindings } from '../lib/queries'
import { PageHead, Severity, Stat, Skel, Empty, Money, RiskBar } from '../components/ui'
import { thaiDate } from '../lib/format'

export function Dashboard() {
  const kpis = useDashboard()
  const findings = useFindings({ limit: '6' })
  const engagements = useEngagements()

  const d = kpis.data
  const list = findings.data ?? []
  const total = d?.findings_total ?? 0
  const highShare = d && total > 0 ? Math.round(((d.high + d.critical) / total) * 100) : 0

  return (
    <>
      <PageHead
        title="ภาพรวมการตรวจสอบ"
        sub={`รอบปัจจุบัน · ${thaiDate(new Date().toISOString())} · สกุลเงิน THB`}
        actions={<Link className="btn" to="/engagements">เปิดโครงการใหม่</Link>}
      />

      <div className="grid-4 stagger">
        <Stat label="ความเสี่ยงที่เปิดอยู่" value={kpis.isLoading ? <Skel h={38} w={90} /> : (d?.findings_open ?? 0)}
          foot={d ? <><span className="gold">{highShare}%</span> เป็นระดับสูงขึ้นไป</> : null} />
        <Stat label="มูลค่าที่มีความเสี่ยง" value={kpis.isLoading ? <Skel h={38} w={140} /> : <Money satang={d?.amount_at_risk_satang ?? 0} compact />}
          foot="รวมทุกข้อสังเกตที่ยังไม่ปิด" />
        <Stat label="รายการเดินบัญชี" value={kpis.isLoading ? <Skel h={38} w={80} /> : (d?.transactions ?? 0)} unit="รายการ"
          foot={`${d?.clients ?? 0} นิติบุคคล · ${d?.engagements ?? 0} โครงการ`} />
        <Stat label="ระดับวิกฤต" value={kpis.isLoading ? <Skel h={38} w={50} /> : (d?.critical ?? 0)}
          foot="ต้องได้รับการอนุมัติสองชั้น" />
      </div>

      <div className="grid-main" style={{ marginTop: 16 }}>
        <div className="card">
          <div className="card-pad" style={{ paddingBottom: 6, display: 'flex', alignItems: 'center' }}>
            <div>
              <div className="card-label">ข้อสังเกตที่มีนัยสำคัญ</div>
              <div className="heading-m" style={{ marginTop: 6 }}>เรียงตามคะแนนความเสี่ยง</div>
            </div>
            <Link to="/findings" className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}>ดูทั้งหมด</Link>
          </div>
          {findings.isLoading ? (
            <div style={{ padding: 20 }} className="stack"><Skel h={44} /><Skel h={44} /><Skel h={44} /></div>
          ) : list.length === 0 ? (
            <Empty title="ยังไม่พบข้อสังเกต" body="เริ่มตรวจสอบโครงการเพื่อสร้างข้อสังเกตชุดแรก" />
          ) : (
            <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
              <table className="data">
                <thead>
                  <tr><th>ข้อสังเกต</th><th>ระดับ</th><th style={{ width: 118 }}>คะแนน</th><th style={{ textAlign: 'right' }}>มูลค่า</th></tr>
                </thead>
                <tbody>
                  {list.map((f) => (
                    <tr key={f.id} onClick={() => { window.location.href = `/findings/${f.id}` }}>
                      <td>
                        <div style={{ color: 'var(--color-ivory)' }}>{f.title}</div>
                        <div className="mono dim" style={{ marginTop: 4, fontSize: 11 }}>{f.rule_code}</div>
                      </td>
                      <td><Severity value={f.severity} /></td>
                      <td><RiskBar score={f.risk_score} /></td>
                      <td className="num"><Money satang={f.amount_at_risk_satang} compact /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="stack">
          <div className="card card-pad">
            <div className="card-label">โครงการที่กำลังดำเนินการ</div>
            <div style={{ marginTop: 14 }} className="stack">
              {(engagements.data ?? []).slice(0, 3).map((e) => (
                <div key={e.id} className="row" style={{ alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13 }}>{e.title}</div>
                    <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>
                      {e.code} · {e.transaction_count} รายการ · เปิด {e.open_findings} ข้อ
                    </div>
                  </div>
                  <span className="chip">{e.status}</span>
                </div>
              ))}
              {engagements.isLoading ? <><Skel h={36} /><Skel h={36} /></> : null}
            </div>
          </div>

          <div className="card card-pad">
            <div className="card-label">สัดส่วนความรุนแรง</div>
            <div style={{ marginTop: 18 }}>
              <div className="row" style={{ height: 6, gap: 3 }}>
                {[
                  { k: 'critical', n: d?.critical ?? 0, c: 'var(--color-crimson)' },
                  { k: 'high', n: d?.high ?? 0, c: 'var(--color-amber)' },
                  { k: 'rest', n: Math.max(0, total - (d?.critical ?? 0) - (d?.high ?? 0)), c: 'var(--color-emerald)' },
                ].map((s) => (
                  <span key={s.k} style={{
                    height: '100%', borderRadius: 3, background: s.c,
                    width: total ? `${Math.max(3, (s.n / total) * 100)}%` : '0%', opacity: 0.85,
                  }} />
                ))}
              </div>
              <div className="stack" style={{ marginTop: 16, gap: 10 }}>
                <div className="row" style={{ fontSize: 12 }}><Severity value="critical" /><span style={{ marginLeft: 'auto' }} className="tnum">{d?.critical ?? 0}</span></div>
                <div className="row" style={{ fontSize: 12 }}><Severity value="high" /><span style={{ marginLeft: 'auto' }} className="tnum">{d?.high ?? 0}</span></div>
                <div className="row" style={{ fontSize: 12 }}><Severity value="low" /><span style={{ marginLeft: 'auto' }} className="tnum">{Math.max(0, total - (d?.critical ?? 0) - (d?.high ?? 0))}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
